import { useEffect, useState } from "react";
import InputField from "../../common/InputField";
import { MdClose } from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import Button from "../../common/Button";
import { IoMdClose } from "react-icons/io";
import { FaPaperPlane } from "react-icons/fa";
import { unitConversionAPI } from "../../utlis/apiService";

const PackingForm = ({
  initialData,
  onSave,
  onCancel,
  isEditing,
  setFormData,
  formData,
  isSaving = false,
}) => {
  const today = new Date().toISOString()?.split("T")[0];
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isUnitConvData, setIsUnitConvData] = useState([]);

  useEffect(() => {
    const fetchUnitConvData = async () => {
      try {
        const res = await unitConversionAPI.getAll();
        setIsUnitConvData(
          res.data.map((u) => ({
            value: u.unit_Conv_Id,
            label: u.unit_Conv_Name,
            raw: u.conv_Value,
          })),
        );
      } catch (err) {
        console.error("Error loading dropdowns", err);
      }
    };
    fetchUnitConvData();
  }, []);

  useEffect(() => {
    if (initialData) {
      const updatedForm = {
        bundle_No: initialData.bundle_No || "0",
        packing_Date: initialData.packing_Date
          ? initialData.packing_Date.split("T")[0]
          : today,
        order_No: initialData.order_No || "",
        dieNumber: initialData.dieNumber || "",
        die_Id: initialData.die_Id ?? null,
        batch_Id: initialData.batch_Id || "",
        casting_No: initialData.casting_No || "",
        cut_Length: initialData.cut_Length || "",
        wt_12_FT: initialData.wT_12_FT || "",
        wt_Cut_Length: initialData.wT_Cut_Length || "",
        pcs: initialData.pcs || "",
        total_Weight: initialData.total_Weight || "",
        series_Name: initialData.series_Name || "",
        remark: initialData.remark || "",
        unit_Conv_Id: initialData.unit_Conv_Id || "",
        final_CutLength_weight: initialData.final_CutLength_weight || "",
      };
      setFormData(updatedForm);
    }
  }, [initialData]);

  const calculateWt12FT = (pcs, totalWeight, cutLength) => {
    if (pcs && totalWeight && cutLength) {
      const result = ((totalWeight / pcs) * 12) / cutLength;
      return isNaN(result) ? "" : result.toFixed(2);
    }
    return "";
  };

  const handleChange = (e, field) => {
    const value = typeof e === "object" && e?.target ? e.target.value : e;
    const updatedForm = { ...formData, [field]: value };

    const pcs = parseFloat(field === "pcs" ? value : formData.pcs || 0);
    const totalWeight = parseFloat(
      field === "total_Weight" ? value : formData.total_Weight || 0,
    );
    const cutLength = parseFloat(
      field === "cut_Length" ? value : formData.cut_Length || 0,
    );

    updatedForm.wt_12_FT = calculateWt12FT(pcs, totalWeight, cutLength);

    const selectedUnit = isUnitConvData.find(
      (u) =>
        u.value === (field === "unit_Conv_Id" ? value : formData.unit_Conv_Id),
    );
    const convValue = selectedUnit?.raw || 1;

    const wt12FT = parseFloat(updatedForm.wt_12_FT || 0);
    const wtCutLength = parseFloat(
      field === "wt_Cut_Length" ? value : formData.wt_Cut_Length || 0,
    );

    updatedForm.final_CutLength_weight =
      wt12FT && wtCutLength && convValue
        ? ((wt12FT * wtCutLength) / convValue).toFixed(2)
        : "";

    setFormData(updatedForm);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, formData.bundle_No);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm font-plus">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          className="bg-white border border-gray-200 w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col  rounded-none font-plus"
        >
          {/* Industrial Status Header */}
          <div className="bg-gray-900 text-white px-8 py-6 flex justify-between items-center shrink-0 border-b border-gray-800">
            <div className="flex flex-col">
              <h2 className="text-[14px] font-black text-white tracking-[3px] uppercase">
                {isEditing ? "Edit Packing" : "Add Packing"}
              </h2>

            </div>
            <button
              onClick={onCancel}
              className="group p-2 hover:bg-white/10 transition-all border border-transparent hover:border-white/20"
            >
              <MdClose size={20} className="text-gray-400 group-hover:text-white" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-10 bg-[#fbfcfd] space-y-10"
          >
            {/* Core Registry Metadata */}
            <div className="bg-white border border-gray-200  relative group rounded-none">
              <div className="flex items-center gap-4 px-8 py-4 border-b border-gray-200 bg-gray-50">
                <span className="w-1 h-4 bg-[#3F80F7]"></span>
                <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-[2px]">
                  Packing Details
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 p-8">
                <InputField
                  label="Bundle No"
                  value={formData.bundle_No}
                  disabled
                  className="bg-gray-100 text-gray-400 rounded-none border-gray-200 uppercase"
                />
                <InputField label="Batch ID" value={formData.batch_Id} disabled className="bg-gray-100" />
                <InputField label="Casting No" value={formData.casting_No} disabled className="bg-gray-100" />
                <InputField label="Order No" value={formData.order_No} disabled className="bg-gray-100" />
                <InputField label="Die No" value={formData.dieNumber} disabled className="bg-gray-100 font-black text-[#3F80F7]" />
                <InputField label="Series" value={formData.series_Name} disabled className="bg-gray-100" />
                <InputField label="Length" value={formData.cut_Length} disabled className="bg-gray-100 font-mono" />
                <InputField
                  type="date"
                  label="Packing Date"
                  value={formData.packing_Date}
                  onChange={(val) => handleChange(val, "packing_Date")}
                  className="rounded-none border-gray-200 focus:border-[#3F80F7]"
                />
              </div>
            </div>

            {/* Industrial Metrics & Measurements */}
            <div className="bg-white border border-gray-200  overflow-hidden rounded-none">
              <div className="flex items-center gap-4 px-8 py-4 border-b border-gray-200 bg-gray-50">
                <span className="w-1 h-4 bg-[#3F80F7]"></span>
                <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-[2px]">
                  Measurements & Packing
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10 p-8">
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
                  <InputField
                    label="Total Weight"
                    value={formData.total_Weight}
                    onChange={(val) => handleChange(val, "total_Weight")}
                    className="focus:border-[#3F80F7] rounded-none"
                    unit="KG"
                  />
                  <InputField
                    label="Pcs"
                    value={formData.pcs}
                    onChange={(val) => handleChange(val, "pcs")}
                    className="focus:border-[#3F80F7] rounded-none font-black"
                    unit="PCS"
                  />
                  <div className="relative group">
                    <InputField
                      label="WT/12 FT"
                      value={formData.wt_12_FT}
                      disabled
                      className="bg-gray-50 font-black text-gray-900 rounded-none border-gray-200"
                    />
                  </div>
                  <InputField
                    label="Final Cut Length"
                    value={formData.wt_Cut_Length}
                    onChange={(val) => handleChange(val, "wt_Cut_Length")}
                    className="focus:border-[#3F80F7] rounded-none"
                    unit="MM"
                  />
                  <InputField
                    type="select"
                    label="Unit"
                    options={isUnitConvData}
                    value={formData.unit_Conv_Id}
                    onChange={(val) => handleChange(val, "unit_Conv_Id")}
                    className="rounded-none border-gray-200 focus:border-[#3F80F7]"
                  />
                  <div className="relative group">
                    <InputField
                      label="Calculated Weight (Auto)"
                      value={formData.final_CutLength_weight}
                      disabled
                      className="bg-gray-900 font-black text-[#3F80F7] rounded-none border-gray-800"
                    />
                    <div className="absolute top-0 right-0 p-1 text-[8px] font-black text-white/40 uppercase tracking-tighter">Validation Output</div>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[2px] mb-3">
                    Remarks
                  </label>
                  <textarea
                    className="w-full flex-1 p-5 bg-gray-50 border border-gray-200 text-[12px] font-medium focus:border-[#3F80F7] transition-all outline-none rounded-none resize-none placeholder:text-gray-300"
                    placeholder="Document fulfillment anomalies or structural deviations..."
                    value={formData.remark}
                    onChange={(e) => handleChange(e.target.value, "remark")}
                  />
                </div>
              </div>
            </div>

            {/* Action Commit Commit Bar */}
            <div className="flex justify-between items-center pt-10 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-8 py-3.5 text-[10px] font-black text-gray-500 uppercase tracking-[3px] hover:text-red-500 transition-all border border-gray-200 hover:border-red-200 rounded-none"
              >
                Cancel
              </button>

              <div className="flex items-center gap-4">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-12 py-3.5 bg-gray-900 hover:bg-black text-white border border-gray-900 font-black text-[10px] uppercase tracking-[3px] transition-all rounded-none  "
                >
                  {isSaving ? "SAVING..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PackingForm;
